<template>
  <div>
    <div class="d-flex flex-row mb-5">
      <div class="block">
        <v-tabs v-model="tab" color="primary" direction="vertical" hide-slider>
          <v-tab class="mb-2" value="document-type">
            <v-icon start>mdi-swap-vertical</v-icon>
            Типи документів
          </v-tab>
          <v-tab class="mb-2" value="requisites">
            <v-icon start>mdi-file-document-multiple-outline</v-icon>
            Загальні реквізити
          </v-tab>
          <v-tab v-if="!createAction" class="mb-2" value="contractors">
            <v-icon start>mdi-badge-account-outline</v-icon>
            Контрагенти
          </v-tab>
          <v-tab v-if="!createAction" class="mb-2" value="users">
            <v-icon start>mdi-badge-account-outline</v-icon>
            Співробітники
          </v-tab>
        </v-tabs>
      </div>

      <div class="w-100 ml-4">
        <v-window v-model="tab">
          <v-window-item value="document-type">
            <b-select
              v-model="document.typeId"
              :error-messages="getErrorMessages(v$.typeId)"
              :items="$directory.get('documentTypes', document.type)"
              :loading="$loading.isLoading('documentTypes')"
              class="mb-1"
              item-title="name"
              item-value="id"
              label="Тип документа"
              placeholder="Оберіть зі списку"
              @blur="v$.typeId.$touch()"
              @focus="$directory.fill('documentTypes')">
            </b-select>
          </v-window-item>
          <v-window-item value="requisites">
            <b-input
              v-model="document.name"
              class="mb-1"
              label="Назва документа"
              placeholder="Введіть текст">
            </b-input>
            <b-input
              v-model="document.number"
              class="mb-1"
              label="Номер документа"
              placeholder="Введіть номер">
            </b-input>
          </v-window-item>
          <v-window-item value="contractors">
            <b-form :submit="pushContractor" hide-action-buttons>
              <b-autocomplete
                v-model="selectedContractorId"
                :item-title="
                  contractor => `${contractor?.name} (${contractor?.code})`
                "
                :items="contractorList"
                :loading="$loading.isLoading('searchContractors')"
                class="mb-1"
                item-value="id"
                label="Контрагент"
                placeholder="Назва/ЄДРПОУ, введіть мін. 3 символи"
                @update:search="searchContractors">
                <template #append>
                  <v-btn type="submit"> Додати </v-btn>
                </template>
              </b-autocomplete>
            </b-form>

            <v-card
              v-for="contractor in document.contractors"
              :key="contractor.id"
              class="mb-3"
              variant="tonal">
              <v-card-text>
                <div class="d-flex align-center justify-space-between">
                  <div style="font-size: 1rem">
                    <div>
                      <div class="text-disabled mb-3">
                        {{ contractor.contractor.code }}
                      </div>
                      <div>{{ contractor.contractor.name }}</div>
                    </div>
                  </div>
                  <div>
                    <v-btn
                      color="error"
                      density="compact"
                      icon="mdi-delete-forever"
                      rounded="circle"
                      variant="outlined"
                      :loading="
                        $loading.isLoading(`removeContractor_${contractor.id}`)
                      "
                      @click="removeContractor(contractor.id)"></v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>
            <div
              v-if="!document.contractors?.length"
              class="text-disabled text-center">
              Контрагенти відсутні
            </div>
          </v-window-item>
          <v-window-item value="users">
            <b-form :submit="pushUser" hide-action-buttons>
              <b-autocomplete
                v-model="selectedUserId"
                :items="$directory.get('userList')"
                :loading="$loading.isLoading('userList')"
                class="mb-1"
                item-title="name"
                item-value="id"
                label="Співробітник"
                placeholder="Оберіть зі списку"
                @focus="$directory.fill('userList')">
                <template #append>
                  <v-btn type="submit">Додати</v-btn>
                </template>
              </b-autocomplete>
            </b-form>

            <v-card
              v-for="user in document.users"
              :key="user.id"
              class="mb-3"
              variant="tonal">
              <v-card-text>
                <div class="d-flex align-center justify-space-between">
                  <div style="font-size: 1rem">
                    <div class="text-disabled mb-3">{{ user.user.post }}</div>
                    <div>
                      {{ getPersonName(user.user, { initials: true }) }}
                    </div>
                  </div>
                  <div class="d-flex align-center gap-3">
                    <v-switch
                      v-model="user.isSignature"
                      color="primary"
                      hide-details
                      label="Підписує"
                      :loading="$loading.isLoading(`setSignature_${user.id}`)"
                      @update:model-value="
                        setSignature(user.id, $event)
                      "></v-switch>
                    <v-btn
                      color="error"
                      density="compact"
                      icon="mdi-delete-forever"
                      rounded="circle"
                      variant="outlined"
                      :loading="$loading.isLoading(`removeUser_${user.id}`)"
                      @click="removeUser(user.id)"></v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>
            <div
              v-if="!document.users?.length"
              class="text-disabled text-center">
              Співробітники відсутні
            </div>
          </v-window-item>
        </v-window>
        <FilesUploader
          v-if="createAction"
          v-model="document.document"
          :error-messages="getErrorMessages(v$.document)"
          :multiple="false"
          :v$="v$.document"
          class="mb-5 mt-3"
          dropper
          label="Оберіть документи"
          main-folder="Документы"
          name-folder="template"
          @update:model-value="submit().then(res => dialog.submit(res))" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { axios, required, useStore, useVuelidate } from '@/plugins/index.js'
import { computed, ref } from 'vue'
import {
  urlDocumentSignatureCreate,
  urlDocumentSignatureUpdate,
  urlDocumentSignatureUserCreate,
  urlDocumentSignatureUserDelete,
  urlDocumentSignatureUserSetSignature,
  urlDocumentSignatureContractorCreate,
  urlDocumentSignatureContractorDelete,
} from '@/utils/urls.js'
import { contractorList as contractorListRequest } from '@/requests/contractor'
import { cachedObject, getPersonName } from 'best-modules/utils'
import { getErrorMessages } from '@/utils/helpers.js'
import FilesUploader from '@/components/filesUploader/FilesUploader.vue'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import { debounce, pick } from 'lodash'
import { fillDirectory } from '@/directory'
import { BForm } from 'best-modules/components'

export default {
  components: {
    FilesUploader,
    BForm,
  },
  props: {
    dialog: {
      type: Object,
      required: true,
    },
  },
  methods: { getErrorMessages, getPersonName },
  async setup(props) {
    const store = useStore()
    const createAction = props.dialog.action === 'create'
    const userId = computed(() => store.state.user.userData?.id)
    const tab = ref('document-type')

    const document = cachedObject({
      id: void [0],
      authorId: void [0],
      status: void [0] as any,
      name: null,
      number: null,
      document: null,
      action: void [0],
      typeId: 1,
      type: void [0],
      contractors: [] as any[],
      users: [] as any[],
    })

    document.$setIgnore('contractors', 'users')

    const readonly = computed(() => {
      if (props.dialog.action === 'create') {
        return false
      } else {
        const isAuthor = document.authorId === userId.value
        return document.status?.id !== 1 || !isAuthor
      }
    })

    if (createAction) {
      await fillDirectory('documentTypes')
    } else {
      document.$set({
        ...(props.dialog.dialogData.document || {}),
        // users: (props.dialog.dialogData.document?.users || []).map(u => {
        //   return {
        //     ...pick(u.user, ['id', 'post', 'department']),
        //     name: getPersonName(u.user),
        //     isSignature: u.isSignature,
        //   }
        // }),
        // contractors: (props.dialog.dialogData.document?.contractors || []).map(
        //   c => {
        //     return pick(c.contractor, ['id', 'name', 'code'])
        //   }
        // ),
      })
    }

    const rules = computed(() => {
      return {
        typeId: { required },
        document: { required },
      }
    })
    const v$ = useVuelidate(rules, document)

    const submit = async () => {
      if (createAction) {
        return axios
          .post(
            urlDocumentSignatureCreate(),
            pick(document.$object, ['document', 'name', 'number', 'typeId'])
          )
          .then(res => {
            setSnackbar({ text: 'Документ успішно створено' })

            return res
          })
      } else {
        return axios
          .put(
            urlDocumentSignatureUpdate(document.id),
            pick(document.$object, ['name', 'number', 'typeId'])
          )
          .then(res => {
            setSnackbar({ text: 'Документ оновлено' })

            return res
          })
      }
    }

    const disabled = computed(() => {
      return readonly
    })

    const selectedContractorId = ref<any>(null)
    const contractorList = ref<any[]>([])

    const searchContractors = debounce((search: string) => {
      handleAsync('searchContractors', () => {
        if (search && !selectedContractorId.value) {
          return contractorListRequest({ search }).then(res => {
            contractorList.value = res
          })
        } else {
          contractorList.value = []
        }
      })
    }, 500)
    const pushContractor = async (): Promise<void> => {
      if (
        selectedContractorId.value &&
        !document.contractors
          .map(c => c.id)
          .includes(selectedContractorId.value)
      ) {
        return handleAsync('pushContractor', () => {
          return axios
            .post(urlDocumentSignatureContractorCreate(), {
              contractorId: selectedContractorId.value,
              documentId: document.id,
            })
            .then(res => {
              document.contractors.push(res.data)
              selectedContractorId.value = null
            })
        })
      }
    }
    const removeContractor = (contractorId: number): Promise<void> => {
      return handleAsync(`removeContractor_${contractorId}`, () => {
        return axios
          .delete(urlDocumentSignatureContractorDelete(contractorId))
          .then(() => {
            document.contractors = document.contractors.filter(
              c => c.id !== contractorId
            )
          })
      })
    }

    const selectedUserId = ref<any>(null)
    const pushUser = async (): Promise<void> => {
      if (
        selectedUserId.value &&
        !document.users.map(u => u.id).includes(selectedUserId.value)
      ) {
        return handleAsync('pushUser', () => {
          return axios
            .post(urlDocumentSignatureUserCreate(), {
              userId: selectedUserId.value,
              documentId: document.id,
              isSignature: false,
            })
            .then(res => {
              document.users.push(res.data)
              selectedUserId.value = null
            })
        })
      }
    }
    const setSignature = (userId: number, isSignature: boolean) => {
      return handleAsync(`setSignature_${userId}`, () => {
        return axios.patch(urlDocumentSignatureUserSetSignature(userId), {
          isSignature,
        })
      })
    }
    const removeUser = (userId: number): Promise<void> => {
      return handleAsync(`removeUser_${userId}`, () => {
        return axios.delete(urlDocumentSignatureUserDelete(userId)).then(() => {
          document.users = document.users.filter(u => u.id !== userId)
        })
      })
    }

    return {
      v$,
      userId,
      document,
      createAction,
      submit,
      disabled,
      readonly,
      tab,
      selectedContractorId,
      contractorList,
      searchContractors,
      pushContractor,
      removeContractor,
      selectedUserId,
      pushUser,
      setSignature,
      removeUser,
    }
  },
}
</script>

<style lang="scss" scoped>
.block {
  background: #f4f6f8;
  padding: 15px 8px;
  border: 1px dashed #80808059;
}
</style>
